import React from 'react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Brain, 
  Building2,
  ArrowLeft,
  Clock,
  Calendar,
  CheckCircle,
  Zap,
  Target,
  TrendingUp,
  Search,
  Bell,
  FileText,
  Sparkles
} from 'lucide-react';

export default function IAMarchesArticle() {
  return (
    <>
      <SEO 
        title="IA et Marchés Publics | Guide 2026"
        description="Comment l'intelligence artificielle révolutionne les marchés publics : veille automatisée, analyse de documents, recommandations personnalisées."
        path="/blog/ia-marches-publics"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article>
        <header className="bg-gradient-to-b from-purple-50 to-white py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                Innovation
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Calendar className="w-4 h-4" />
                20 janvier 2026
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Clock className="w-4 h-4" />
                10 min de lecture
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Comment l'IA Révolutionne la Veille des Marchés Publics
            </h1>
            
            <p className="text-xl text-slate-600">
              L'Intelligence Artificielle transforme la façon dont les entreprises trouvent et 
              répondent aux appels d'offres. Découvrez comment gagner des heures chaque semaine.
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="container mx-auto px-4 max-w-4xl py-12">
          
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg text-slate-700 mb-6">
              La <strong>veille des marchés publics</strong> est chronophage : surveiller plusieurs plateformes, 
              analyser des dizaines d'avis, identifier ceux qui correspondent à votre activité... 
              L'<strong>Intelligence Artificielle</strong> change la donne en automatisant ces tâches 
              et en vous permettant de vous concentrer sur l'essentiel : répondre aux bons marchés.
            </p>

            <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white mb-8">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Brain className="w-12 h-12 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold mb-2">Le saviez-vous ?</h3>
                    <p className="text-white/90">
                      Une entreprise passe en moyenne <strong>8 heures par semaine</strong> à faire de la veille 
                      manuelle sur les marchés publics. L'IA peut réduire ce temps à <strong>30 minutes</strong>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Les limites de la veille manuelle
            </h2>
            
            <p className="text-slate-600 mb-6">
              La méthode traditionnelle de veille des appels d'offres présente plusieurs problèmes :
            </p>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                { icon: Clock, title: 'Chronophage', desc: 'Consulter 5-10 plateformes quotidiennement prend des heures' },
                { icon: Search, title: 'Éparpillement', desc: 'Les marchés sont dispersés sur de nombreuses sources' },
                { icon: Target, title: 'Manque de pertinence', desc: 'Difficile de filtrer parmi des centaines d\'avis' },
                { icon: Bell, title: 'Risque d\'oubli', desc: 'Facile de manquer un marché intéressant' },
              ].map((item, i) => (
                <Card key={`item-${i}`} className="border-red-200 bg-red-50">
                  <CardContent className="p-4 flex items-start gap-3">
                    <item.icon className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-red-900">{item.title}</p>
                      <p className="text-sm text-red-700">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Comment l'IA transforme la veille
            </h2>

            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Search className="w-8 h-8 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">1. Agrégation automatique</h3>
                  <p className="text-slate-600">
                    L'IA collecte automatiquement les marchés de <strong>toutes les sources</strong> : 
                    BOAMP, DECP, TED Europa, plateformes privées... Plus besoin de naviguer entre 10 sites différents.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Brain className="w-8 h-8 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">2. Matching intelligent</h3>
                  <p className="text-slate-600">
                    Les algorithmes de <strong>Natural Language Processing (NLP)</strong> analysent votre profil 
                    d'entreprise et comparent avec chaque marché pour calculer un <strong>score de compatibilité</strong>.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-8 h-8 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">3. Recommandations personnalisées</h3>
                  <p className="text-slate-600">
                    Au lieu de 100 marchés à trier, vous recevez les <strong>3-10 plus pertinents</strong> pour 
                    votre entreprise, avec une explication de pourquoi ils correspondent.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Bell className="w-8 h-8 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">4. Alertes proactives</h3>
                  <p className="text-slate-600">
                    Soyez notifié <strong>instantanément</strong> quand un marché correspondant à vos critères 
                    est publié. Plus de risque de manquer une opportunité.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Les bénéfices concrets de l'IA
            </h2>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-violet-600 mb-2">-80%</div>
                  <p className="text-slate-600">de temps de veille</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-violet-600 mb-2">+50%</div>
                  <p className="text-slate-600">de marchés pertinents identifiés</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-violet-600 mb-2">6+</div>
                  <p className="text-slate-600">sources agrégées automatiquement</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-green-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Témoignage client
                </h3>
                <blockquote className="text-green-800 italic">
                  "Avant, je passais mes lundis matin à faire de la veille. Maintenant, je reçois 
                  mes recommandations personnalisées et je peux directement me concentrer sur la 
                  rédaction des offres. J'ai gagné une journée par semaine."
                </blockquote>
                <p className="text-green-700 mt-3 font-medium">— Marie D., Dirigeante PME BTP</p>
              </CardContent>
            </Card>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Au-delà de la veille : l'IA pour répondre
            </h2>

            <p className="text-slate-600 mb-6">
              L'Intelligence Artificielle ne s'arrête pas à la veille. Elle peut également vous aider à :
            </p>

            <ul className="space-y-4 mb-8">
              {[
                { title: 'Analyser les DCE', desc: 'Extraction automatique des critères clés et des exigences' },
                { title: 'Évaluer vos chances', desc: 'Score de probabilité de succès basé sur vos références' },
                { title: 'Générer des trames', desc: 'Assistance à la rédaction du mémoire technique' },
                { title: 'Optimiser vos prix', desc: 'Analyse des marchés similaires pour ajuster votre offre' },
              ].map((item, i) => (
                <li key={`item-${i}`} className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-slate-900">{item.title}</span>
                    <span className="text-slate-600"> — {item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Comment démarrer avec l'IA
            </h2>

            <ol className="space-y-6">
              {[
                { step: 1, title: 'Créez votre profil', desc: 'Renseignez votre secteur, région, taille et mots-clés représentatifs de votre activité.' },
                { step: 2, title: 'Laissez l\'IA travailler', desc: 'L\'algorithme analyse les marchés en continu et calcule les scores de compatibilité.' },
                { step: 3, title: 'Recevez vos recommandations', desc: 'Consultez vos marchés recommandés classés par pertinence.' },
                { step: 4, title: 'Concentrez-vous sur les réponses', desc: 'Utilisez le temps gagné pour préparer des offres de qualité.' },
              ].map((item) => (
                <li key={item.step} className="flex items-start gap-4">
                  <span className="w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    {item.step}
                  </span>
                  <div>
                    <h4 className="font-bold text-slate-900">{item.title}</h4>
                    <p className="text-slate-600">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Conclusion</h2>
            <p className="text-slate-600 mb-4">
              L'<strong>Intelligence Artificielle</strong> n'est plus réservée aux grandes entreprises. 
              Des outils comme <strong>Mon DCE Express</strong> la rendent accessible à toutes les PME 
              souhaitant optimiser leur veille des marchés publics.
            </p>
            <p className="text-slate-600">
              En automatisant les tâches répétitives et en apportant une analyse intelligente, l'IA 
              vous permet de vous concentrer sur ce qui compte vraiment : <strong>remporter des marchés</strong>.
            </p>
          </section>

        </div>

        {/* CTA */}
        <section className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Passez à la veille intelligente
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Essayez gratuitement et recevez vos 3 premières recommandations personnalisées.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100">
                <Sparkles className="w-5 h-5 mr-2" />
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </section>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center text-sm">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
